import React, {useState, useEffect} from 'react';
import clsx from 'clsx';
import {castArray, isEmpty, join, omit} from "lodash";
import {getSessionFacts, writeSessionFactsToStorage} from "../Session/sessionFacts";
import {KNOWN_PARAMS} from "../lib/params";

/**
 * The presence of pageTriggers param, in this request or the initial request,
 * will make this widget visible. Clicking the 'hide' link will
 * @type {string}
 */
const DEBUG_PARAM = KNOWN_PARAMS.DEBUG_PAGE_TRIGGERS;

export default function DebugPageTriggers({results, sessionFacts, ...props}) {
  const [visible, setVisible] = useState(() => !!getDebugParamValue(sessionFacts));

  if (!visible) return null;

  const handleClick = () => {
    // scrub from local storage so it doesn't come back on next page load.
    if (sessionFacts) {
      sessionFacts = Object.assign({}, sessionFacts);
      sessionFacts.firstPageParams  = omit(sessionFacts.firstPageParams, DEBUG_PARAM);
      sessionFacts.currentParams  = omit(sessionFacts.currentParams, DEBUG_PARAM);

      writeSessionFactsToStorage(sessionFacts);
    }

    setVisible(false);
  }

  return (<div className="container mx-auto">
    <div className="text-right text-small">
      <a onClick={handleClick}>
        <span className="fa fa-times-circle text-xl text-pink-800"/>
      </a>
    </div>
    <div>
      {(results || []).map((r, i) => <DebugOneTrigger key={i} result={r}/>)}
    </div>
    <pre>
      {JSON.stringify(sessionFacts, null, 2)}
    </pre>
  </div>);
}

function getDebugParamValue(sessionFacts) {
  if (isEmpty(sessionFacts)) return false;

  const {currentParams = {}, firstPageParams = {}} = sessionFacts;

  return (currentParams && currentParams[DEBUG_PARAM]) ||
    (firstPageParams && firstPageParams[DEBUG_PARAM]);
}

function DebugOneTrigger({result, ...props}) {
  const {trigger = {name: 'unknown'}, testResults = []} = result;

  return <div>
    <h3 className="font-bold text-xl py-2">
      trigger: {trigger.name}
    </h3>

    <table>
      <thead>
      <tr>
        <th className="px-2"></th>
        <th className="px-2">id</th>
        <th className="px-2">flavour</th>
        <th className="px-2">actual</th>
        <th className="px-2">op</th>
        <th className="px-2">target</th>
      </tr>
      </thead>
      <tbody>
      {testResults.map((r,i) => <ResultRow r={r} key={i}/>)}
      </tbody>
    </table>
  </div>
}

function StatusIndicator({r}) {
  if (r.error) {
    return <span className="fas fa-exclamation-triangle text-xl text-red-800"/>
  } else if (r.success) {
    return <span className="fas fa-check-circle text-xl text-green-800"/>
  } else {
    return <span className="far fa-frown text-xl text-gray-600"/>
  }
}

function ResultRow({r}) {
  const {condition = {}} = r;

  return <tr>
    <td className="py-1 px-2"><StatusIndicator r={r}/></td>
    <td className="py-1 px-2">{condition.id}</td>
    <td className="py-1 px-2">
      {condition.flavour}
      {condition.paramName ? `: ${condition.paramName}` : ''}
    </td>

    <td className="py-1 px-2">
      {r.error &&
        <div className="font-bold text-red-800">{r.message || 'ERROR'}</div>
      }
      <div className="text-small">{join(castArray(r.actualValues), "; ")}</div>
    </td>
    <td className="py-1 px-2">
      {condition.negating ? <b> NOT </b> : ''}
      {condition.operator}
    </td>

    <td className="py-1 px-2">
      {r.matched && <div className="font-bold">
        MATCHED: {r.target}
      </div>}
      <div className="text-small">{join(castArray(r.targetValues), "; ")}</div>
    </td>
  </tr>
}
